/* Created by Interactive 63 - https://interactive63.com */
/**
 * A small keyboard-accessible context menu with submenus. Arrow keys move, Right and Enter open
 * submenus or activate, Left closes a submenu, and Escape closes everything.
 */
import { h } from '../../ui/dom';

export interface MenuItem {
  label?: string;
  onSelect?: () => void;
  disabled?: boolean;
  separator?: boolean;
  items?: MenuItem[];
}

let closeCurrent: (() => void) | null = null;

/** Returns the enabled item buttons directly inside a menu. */
function itemButtons(menu: HTMLElement): HTMLButtonElement[] {
  return Array.from(
    menu.querySelectorAll<HTMLButtonElement>(':scope > .menu-row > .menu-item'),
  ).filter((button) => !button.disabled);
}

/** Builds one menu level. `close` shuts the whole menu; `parentButton` is the item that opened it. */
function buildMenu(
  items: readonly MenuItem[],
  close: () => void,
  parentButton?: HTMLButtonElement,
): HTMLElement {
  const menu = h('div', {
    class: ['context-menu', parentButton ? 'is-submenu' : ''],
    role: 'menu',
  });

  for (const item of items) {
    if (item.separator) {
      menu.append(h('div', { class: 'menu-separator', role: 'separator' }));
      continue;
    }
    const button = h(
      'button',
      {
        type: 'button',
        class: 'menu-item',
        role: 'menuitem',
        tabindex: -1,
        disabled: item.disabled ?? false,
        'aria-haspopup': item.items ? 'menu' : null,
        'aria-expanded': item.items ? 'false' : null,
      },
      h('span', null, item.label ?? ''),
      item.items ? h('span', { class: 'menu-arrow', 'aria-hidden': 'true' }, '›') : null,
    );
    const row = h('div', { class: 'menu-row' }, button);

    if (item.items) {
      const submenu = buildMenu(item.items, close, button);
      submenu.hidden = true;
      row.append(submenu);
      /** Shows the submenu and optionally moves focus into it. */
      const open = (focus: boolean): void => {
        submenu.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        if (focus) itemButtons(submenu)[0]?.focus();
      };
      button.addEventListener('click', () => {
        open(true);
      });
      row.addEventListener('pointerenter', () => {
        open(false);
      });
      row.addEventListener('pointerleave', () => {
        submenu.hidden = true;
        button.setAttribute('aria-expanded', 'false');
      });
      button.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight' || event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          open(true);
        }
      });
    } else {
      button.addEventListener('click', () => {
        close();
        item.onSelect?.();
      });
    }
    menu.append(row);
  }

  menu.addEventListener('keydown', (event) => {
    const buttons = itemButtons(menu);
    const index = buttons.findIndex((b) => b === document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      buttons[(index + 1) % buttons.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      buttons[(index - 1 + buttons.length) % buttons.length]?.focus();
    } else if (event.key === 'ArrowLeft' && parentButton) {
      event.preventDefault();
      event.stopPropagation();
      menu.hidden = true;
      parentButton.setAttribute('aria-expanded', 'false');
      parentButton.focus();
    }
  });
  return menu;
}

/** Opens a context menu at a screen position and returns a function that closes it. */
export function showContextMenu(x: number, y: number, items: readonly MenuItem[]): () => void {
  closeCurrent?.();
  let closed = false;
  const previousFocus =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;

  /** Removes the menu and listeners, returning focus to where it was. */
  const close = (): void => {
    if (closed) return;
    closed = true;
    menu.remove();
    document.removeEventListener('pointerdown', onOutside, true);
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('blur', close);
    if (closeCurrent === close) closeCurrent = null;
    previousFocus?.focus();
  };
  const menu = buildMenu(items, close);
  menu.style.left = `${x}px`;
  menu.style.top = `${y}px`;

  /** Closes the menu when the pointer goes down outside it. */
  const onOutside = (event: PointerEvent): void => {
    if (!(event.target instanceof Node) || !menu.contains(event.target)) close();
  };
  /** Closes the menu on Escape. */
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  };

  document.body.append(menu);
  const box = menu.getBoundingClientRect();
  menu.style.left = `${Math.max(4, Math.min(x, window.innerWidth - box.width - 4))}px`;
  menu.style.top = `${Math.max(4, Math.min(y, window.innerHeight - box.height - 4))}px`;
  document.addEventListener('pointerdown', onOutside, true);
  document.addEventListener('keydown', onKey, true);
  window.addEventListener('blur', close);
  closeCurrent = close;
  itemButtons(menu)[0]?.focus();
  return close;
}
